import { pushIdTime, toTime } from 'lib/content'

// Pure helpers for devices: which locations are free, and what counts as the same device code.

// Device codes are compared without the spaces around them and without regard to upper / lower
// case: "MEGAGYM6", " megagym6 " and "megagym6" are ONE code. The stored code is the trimmed one.
export const normalizeDeviceCode = (code) => String(code === undefined || code === null ? '' : code).trim()
export const deviceCodeKey = (code) => normalizeDeviceCode(code).toLowerCase()

// A location can belong to ONE device.

// Ids of the locations that already have a device. `exceptDeviceId` is the device being
// edited: its own location does not count as taken.
export const takenLocationIds = (devices, exceptDeviceId) =>
  new Set((devices || [])
    .filter(device => device.locationId && device.id !== exceptDeviceId)
    .map(device => device.locationId))

// The locations a device can be given: the ones without any device, plus (when the device
// is being edited) the one it already has, even if an older record shares it with another
// device. `device` is the device as it was before editing, or undefined for a new one.
export const selectableLocations = (locations, devices, device) => {
  const taken = takenLocationIds(devices, device?.id)
  return (locations || [])
    .filter(location => !taken.has(location.id) || location.id === device?.locationId)
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
}

// "Updated" of a device: when it was created or last saved in the admin app. Saving writes that
// moment to deviceUpdated/{clientId}/{deviceId} (lib/firebase.js), and hooks/useDevice puts it on the
// device as `updatedAt`. A device not saved since then has none: its creation time is read from the
// record id instead (a Firebase push id starts with the moment it was created, like for content).
// The pings of the player do not count. Milliseconds since 1970, or null.
export const deviceUpdatedTime = (device, now) => toTime(device?.updatedAt) ?? pushIdTime(device?.id, now)

// Filters of the Devices page. The value a device is filtered by, or null when it has none.
// A device that has no value is chosen with NO_VALUE ("Unknown" in the list).
export const NO_VALUE = '__none__'

const textKey = (value) => {
  const text = String(value === undefined || value === null ? '' : value).trim()
  return text === '' ? null : text
}

// The version of the player app and the model, as the device reports them
export const appVersionKey = (device) => textKey(device?.appVersion)
export const modelKey = (device) => textKey(device?.model)

// The Android version: "11", "11.0" and "11.0.0" are ONE version (the devices report it in all
// three forms), so trailing ".0" parts are dropped. Anything that is not a version number is
// kept as it is.
export const androidVersionKey = (device) => {
  const text = textKey(device?.androidVersion)
  return text !== null && /^\d+(\.\d+)*$/.test(text) ? text.replace(/(\.0+)+$/, '') : text
}

// The choices of one filter: every value the devices have, with how many devices have it, and
// "Unknown" for the devices without one. [{ value, label }] (the filter sorts them by label).
export const filterOptions = (devices, keyOf) => {
  const counts = new Map()
  let missing = 0
  for (const device of devices || []) {
    const key = keyOf(device)
    if (key === null) missing++
    else counts.set(key, (counts.get(key) || 0) + 1)
  }
  const options = [...counts].map(([key, count]) => ({ value: key, label: `${key} (${count})` }))
  if (missing > 0) options.push({ value: NO_VALUE, label: `Unknown (${missing})` })
  return options
}

// Does the device pass a filter? `chosen` empty = any value.
export const matchesFilter = (device, keyOf, chosen) =>
  !chosen || chosen.length === 0 || chosen.includes(keyOf(device) ?? NO_VALUE)
